import { forwardRef, useMemo } from 'react';

import { bindToggle } from 'material-ui-popup-state/core';
import { usePopupState } from 'material-ui-popup-state/hooks';

import { setRef } from '@cast/utils';

import { ChipInput, ChipInputProps } from '../controls/chip-input';
import {
  DropdownContext,
  DropdownPopper,
  DropdownProps,
  useDropdownWithAutocomplete,
} from '../controls/dropdown';

export type ChipInputWithDropdownProps = {
  value: string[];
  onChange: (newValue: string[]) => void;
  options: string[];
} & ChipInputProps;

export const ChipInputWithDropdown = forwardRef<
  HTMLElement,
  ChipInputWithDropdownProps
>(
  (
    {
      options,
      value,
      onChange,
      testId = 'chip-input-with-dropdown',
      disabled,
      readOnly,
      ...chipInputProps
    },
    ref
  ) => {
    const popupState = usePopupState({
      variant: 'popover',
      popupId: 'dropdown',
      disableAutoFocus: true,
    });

    const dropdownProps: DropdownProps<string, string, 'multiple'> = useMemo(
      () => ({
        options,
        checkable: true,
        multiple: true,
        optionValue: (o) => o,
        optionKey: (o) => o,
        optionLabel: (o) => o,
        onChange: (_, newValue) => {
          onChange(newValue);
        },
        value,
        testId,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [options, value, testId]
    );

    const autocomplete = useDropdownWithAutocomplete<
      string,
      string,
      'multiple'
    >({
      dropdownProps,
    });

    return (
      <DropdownContext.Provider
        value={{ autocomplete, popupState, dropdownProps }}
      >
        <ChipInput
          onClick={(event) => {
            if (disabled || readOnly) {
              return;
            }
            bindToggle(popupState).onClick(event);
          }}
          chips={autocomplete.states.value?.map((v: string) => ({
            title: v,
            onDestroy:
              disabled || readOnly
                ? undefined
                : (event) => {
                    event.stopPropagation();
                    onChange(
                      autocomplete.states.value?.filter((c) => c !== v) || []
                    );
                  },
            multiline: false,
          }))}
          startIcon={false}
          disabled={disabled}
          readOnly={readOnly}
          {...chipInputProps}
          testId={testId}
          ref={(r) => {
            if (r?.wrapperRef) {
              setRef(ref, r.wrapperRef);
            }
          }}
        />
        <DropdownPopper />
      </DropdownContext.Provider>
    );
  }
);

ChipInputWithDropdown.displayName = 'ChipInputWithDropdown';
